import { useRef, useCallback } from 'react';

/**
 * Swipe navigation hook for floor plan viewer
 *
 * This hook works alongside react-zoom-pan-pinch to detect swipe gestures for page navigation.
 * It uses the onPanning callbacks from TransformWrapper to detect horizontal swipes.
 *
 * Behavior:
 * - Zoomed out (scale <= minZoom): Horizontal swipes navigate between pages
 * - Zoomed in (scale > minZoom): Swipe navigation is DISABLED, only panning works
 *
 * @param {Object} options - Configuration options
 * @param {number} options.currentScale - Current zoom scale
 * @param {number} options.minScale - Minimum zoom scale (fit-to-screen)
 * @param {Function} options.onPageChange - Callback for page navigation: (direction: 'prev'|'next') => void
 * @param {boolean} options.disabled - Disable gesture handling
 * @param {number} options.containerWidth - Width of the viewer container
 */
export function useFloorplanGestures({
    currentScale,
    minScale,
    onPageChange,
    disabled = false,
    containerWidth = 0,
}) {
    // Gesture state tracking
    const gestureRef = useRef({
        panStartX: 0,
        panStartY: 0,
        panStartTime: 0,
        panStartPositionX: 0, // Transform positionX at pan start
        isPanning: false,
        hasTriggeredNavigation: false, // Prevent multiple triggers
    });

    // Thresholds - tuned to be less sensitive
    const THRESHOLDS = {
        DIRECTION_LOCK_FACTOR: 1.5, // |dx| > |dy| * factor = horizontal (increased from 1.3)
        SWIPE_DISTANCE_PCT: 0.4, // 40% of container width (increased from 30%)
        SWIPE_VELOCITY: 0.8, // px/ms (increased from 0.5)
        MIN_SWIPE_DISTANCE: 120, // Minimum distance in pixels (increased from 80)
    };

    /**
     * Called when panning starts
     * We use this to capture the initial state
     */
    const handlePanStart = useCallback(
        (ref, event) => {
            if (disabled) return;

            const state = gestureRef.current;
            const touch = event.touches ? event.touches[0] : event;

            state.panStartX = touch.clientX;
            state.panStartY = touch.clientY;
            state.panStartTime = Date.now();
            state.panStartPositionX = ref.state.positionX;
            state.isPanning = true;
            state.hasTriggeredNavigation = false;
        },
        [disabled]
    );

    /**
     * Called during panning
     * We check if conditions are met for page navigation
     */
    const handlePanning = useCallback(
        (ref, event) => {
            if (
                disabled ||
                !gestureRef.current.isPanning ||
                gestureRef.current.hasTriggeredNavigation
            ) {
                return;
            }

            const state = gestureRef.current;
            const touch = event.touches ? event.touches[0] : event;

            const dx = touch.clientX - state.panStartX;
            const dy = touch.clientY - state.panStartY;
            const duration = Date.now() - state.panStartTime;

            const absDx = Math.abs(dx);
            const absDy = Math.abs(dy);

            // Check if gesture is primarily horizontal
            const isHorizontal = absDx > absDy * THRESHOLDS.DIRECTION_LOCK_FACTOR;

            if (!isHorizontal) {
                // Not a horizontal swipe, let normal panning happen
                return;
            }

            // Calculate threshold distance
            const distanceThreshold =
                containerWidth > 0
                    ? containerWidth * THRESHOLDS.SWIPE_DISTANCE_PCT
                    : THRESHOLDS.MIN_SWIPE_DISTANCE;

            // Calculate velocity
            const velocity = duration > 0 ? absDx / duration : 0;

            // Check if swipe meets thresholds
            const meetsDistanceThreshold = absDx >= distanceThreshold;
            const meetsVelocityThreshold = velocity >= THRESHOLDS.SWIPE_VELOCITY;

            if (!meetsDistanceThreshold && !meetsVelocityThreshold) {
                // Threshold not met yet, continue
                return;
            }

            // Determine if we should allow navigation based on zoom level
            const isZoomedOut = currentScale <= minScale;

            if (isZoomedOut) {
                // When zoomed out, any horizontal swipe triggers navigation
                const direction = dx > 0 ? 'prev' : 'next';
                state.hasTriggeredNavigation = true;

                if (onPageChange) {
                    onPageChange(direction);
                }
            } else {
                // When zoomed in: DISABLE swipe navigation completely
                // User can only pan, no page changes
                // This prevents accidental navigation while exploring the zoomed image
                return;
            }
        },
        [disabled, currentScale, minScale, containerWidth, onPageChange, THRESHOLDS]
    );

    /**
     * Called when panning stops
     * Reset state
     */
    const handlePanStop = useCallback(() => {
        const state = gestureRef.current;
        state.isPanning = false;
        state.hasTriggeredNavigation = false;
    }, []);

    return {
        onPanningStart: handlePanStart,
        onPanning: handlePanning,
        onPanningStop: handlePanStop,
    };
}
